import React, { useEffect, useMemo, useState } from 'react';
import { useAnalytics } from '@context/analytics/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useTranslation } from 'next-i18next';
import { Product } from '@interfaces/models/product';
import PersonalizedHeader from '@components/personalized/personalized-header/personalized-header';
import PersonalizedSort from '@components/personalized/personalized-sort/personalized-sort';
import logger from '@helpers/utils/logger/client';
import { useRouter } from 'next/router';
import ProductFeedLayout from '@components/generic-product-feed/product-feed-layout/product-feed-layout';
import ProductFeed from '@components/generic-product-feed/product-feed/product-feed';
import { AxiosError } from 'axios';
import { datadogRum } from '@datadog/browser-rum-slim';
import { getRecommendedProducts, SortOrder } from '@api/recommendations';
import useUser from '@hooks/user/use-user';

const DEFAULT_SORT_ORDER = 'dateDesc';
const DEFAULT_LIMIT = 20;
const SORT_OPTIONS = {
  relevance: 'RECOMMENDED_PRODUCT.SORT.RELEVANCE',
  dateDesc: 'RECOMMENDED_PRODUCT.SORT.RECENTLY_ADDED',
  recentPriceDrop: 'RECOMMENDED_PRODUCT.SORT.PRICE_DROPS',
  price: 'RECOMMENDED_PRODUCT.SORT.PRICE_LOW_TO_HIGH',
  priceDesc: 'RECOMMENDED_PRODUCT.SORT.PRICE_HIGH_TO_LOW',
};

/**
 * Sorting order from query paramater
 * default order: dateDesc
 * all orders : relevance | dateDesc | recentPriceDrop | price | priceDesc
 */
const sortOrderFromQueryParameter = (queryParam?: string | string[]): SortOrder => {
  const sortOrder = Array.isArray(queryParam) ? queryParam[0] : (queryParam as string);
  if (!sortOrder) {
    return DEFAULT_SORT_ORDER;
  }

  return sortOrder in SORT_OPTIONS ? (sortOrder as SortOrder) : DEFAULT_SORT_ORDER;
};

const RecommendationsWrapper: React.FC = () => {
  const { t } = useTranslation();
  const { updateDataLayer } = useAnalytics();
  const { sendAnalyticEvent: sendProductAnalyticEvent } = useAnalyticEvents('recommendations');
  const { isAuthenticated } = useUser();
  const router = useRouter();
  const { userId, source, campaign, order: sortOrder } = router?.query;
  const sortOrderFromRoute = sortOrderFromQueryParameter(sortOrder);
  const [products, setProducts] = useState<Product[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [totalCountState, setTotalCountState] = useState({
    totalCount: 0,
    isInitialized: false,
  });
  const [order, setOrder] = useState<SortOrder>(sortOrderFromRoute);
  const [isLoading, setLoading] = useState<boolean>(true);
  const { totalCount } = totalCountState;

  const viewMoreVisible = useMemo(() => products.length < totalCount, [products, totalCount]);

  const resetRecommendationsList = () => {
    setLoading(true);
    setProducts([]);
    setOffset(0);
    setTotalCountState({ totalCount: 0, isInitialized: false });

    // TODO: Have to define this user flow (TBD) if we need to enable have to handle the sort as this method is used on sort.
    /* if (DEFAULT_SORT_ORDER !== router?.query?.order) {
      logger.error(`recommendations::logout-login::sortOrderChange-${router?.query?.order}`);
      setOrder(DEFAULT_SORT_ORDER);
      router.query.order = DEFAULT_SORT_ORDER;
      router.push(router);
    } */
  };

  const handleExploreMoreCta = () => {
    sendProductAnalyticEvent('recommendations_empty_state');
    router.push(t('LINKS.NEW_IN'));
  };

  const recommendationEmptyState = useMemo(() => {
    return {
      titleText: 'RECOMMENDED_PRODUCT.EMPTY.TITLE.TEXT',
      subTitleText: 'RECOMMENDED_PRODUCT.EMPTY.SUBTITLE_TEXT',
      clickHandler: handleExploreMoreCta,
      ctaText: 'RECOMMENDED_PRODUCT.EMPTY.CTA_TITLE',
    };
  }, []);

  useEffect(() => {
    const { totalCount, isInitialized } = totalCountState;
    if (!isInitialized || !isAuthenticated) {
      return;
    }
    updateDataLayer({
      env_template: 'my_recommendations',
      screen_name: '/my_recommendations',
      screen_category: 'my_personalized_items',
      screen_subcategory: 'my_recommendations',
      pageUrl: '/my_recommendations',
      sorting: order,
      nb_items: totalCount?.toString() || '0',
    });
  }, [order, totalCountState, isAuthenticated]);

  // TODO: Move to React Query
  useEffect(() => {
    setLoading(true);
    if (isAuthenticated) {
      getRecommendedProducts({
        limit: DEFAULT_LIMIT,
        offset,
        order,
        userId,
        source,
        campaign,
      })
        .then(({ data, meta: { count } }) => {
          if (data) {
            setProducts((prev) => [...prev, ...data]);
            setTotalCountState({ totalCount: count, isInitialized: true });
            setLoading(false);
          }
        })
        .catch((error) => {
          if ((error as AxiosError)?.code === 'ECONNABORTED') {
            datadogRum.addError(error, { userId });
            logger.error(error, 'ECONNABORTED while fetching recommended products!');
            return;
          }
          logger.error(`recommendations::fetchingRecommendations::${error}`);
          // TODO: when userId from deeplink doesn't match with the current logged in user, need to redirect to error page based on status code
          router.push('/_error');
        });
    } else {
      logger.error(`recommendations::resetReco::userNotLoggedIn::`);
      resetRecommendationsList();
    }
  }, [order, offset, isAuthenticated, source, campaign]);

  const handleSortClick = (sortedValue: SortOrder) => {
    if (order != sortedValue) {
      resetRecommendationsList();
      setOrder(sortedValue);
      router.query.order = sortedValue;
      router.push(router);

      sendProductAnalyticEvent('prd_sorting_change', {
        action: sortedValue,
      });
    }
  };

  const RecommendationsHeaderContainer: React.FC = () => {
    return (
      <PersonalizedHeader
        productsCount={totalCount}
        isLoading={isLoading}
        title={t('RECOMMENDED_PRODUCT.TITLE_TEXT')}
        subTitle={totalCount === 1 ? t('RECOMMENDED_PRODUCT.ITEM_TEXT') : t('RECOMMENDED_PRODUCT.ITEMS_TEXT')}
        pageType="recommendations"
      >
        {totalCount > 0 && !isLoading && (
          <PersonalizedSort
            disabled={isLoading || !totalCount}
            options={SORT_OPTIONS}
            value={order}
            onChange={(sortedValue) => handleSortClick(sortedValue)}
            pageType="recommendations"
          />
        )}
      </PersonalizedHeader>
    );
  };

  return (
    <ProductFeedLayout
      header={<RecommendationsHeaderContainer />}
      productList={
        <ProductFeed
          products={products}
          viewMoreVisible={viewMoreVisible}
          viewMoreCTAText="RECOMMENDED_PRODUCT.CTA_LOAD_MORE"
          handleViewMoreClick={setOffset}
          isLoading={isLoading}
          pageType="recommendations"
          emptyState={recommendationEmptyState}
        />
      }
    />
  );
};

export default RecommendationsWrapper;
